import { Button } from '@edsn/pie-components';
import AddIcon from '@iconify/icons-material-symbols/add';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import type { CreateUpdateTeamDto } from '#pie/internal/api/pie-bff';
import { getTeam, usePostTeam } from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';
import { Icon } from '../icon/Icon';
import { Stack } from '../stack/Stack';
import { useToast } from '../toast/ToastContext';

export const CreateTeamForm = () => {
  const { t: tcom } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const { addToast } = useToast();
  const formMethods = useForm<CreateUpdateTeamDto>();

  const { handleSubmit, reset } = formMethods;

  const { mutate, isPending: isLoading } = usePostTeam({
    mutation: {
      onError: (error) => {
        addToast({
          message: tom('toast.error.message.create_team'),
          title: tom('toast.error.title.create_team'),
          type: 'error',
          error: error,
        });
      },
      onSuccess: (data, variables) => {
        if (data.id) {
          queryClient.invalidateQueries({ queryKey: [getTeam.name] });
          queryClient.invalidateQueries({ queryKey: ['/team'] });
          addToast({
            message: tom('toast.success.message.create_team', {
              team: variables.data.name,
            }),
            title: tom('toast.success.title.create_team'),
            type: 'success',
          });
          setIsOpen(false);
          reset();
        }
      },
    },
  });

  const onSubmit: SubmitHandler<CreateUpdateTeamDto> = (data) => {
    mutate({ data });
  };

  const { FormText } = typedFormFields<CreateUpdateTeamDto>();

  return (
    <>
      <Button
        iconStart={<Icon icon={AddIcon} />}
        action="secondary"
        emphasis="bold"
        onClick={() => setIsOpen(true)}
      >
        {tom('button.new_team')}
      </Button>

      {isOpen && (
        <Dialog
          title={tom('button.create.team')}
          size="md"
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap="lg">
                <Stack gap="sm">
                  <FormText
                    autoFocus
                    fullWidth
                    name="name"
                    label={tcom('common.name')}
                    rules={{ required: true }}
                  />
                </Stack>
                <Button
                  isLoading={isLoading}
                  type="submit"
                  action="secondary"
                  emphasis="bold"
                >
                  {tom('button.create.team')}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </Dialog>
      )}
    </>
  );
};
